import { Button, Stack } from '@mui/material';
import { useModal, useToastr } from '@farmshare/ui-components';
import {
  CatalogInfoForm,
  CutForm,
  ExtraForm,
  SpecificationForm,
  TrimForm,
} from '../components';
import type {
  Cut,
  CutsheetCatalog,
  Extra,
  Specification,
  Trim,
} from '../types';
import { removeTypename } from '@farmshare/utils';

interface UseCapabilityHandlersParams {
  capabilities: CutsheetCatalog;
  capabilityId: string;
  addCut: (values: any) => void;
  updateCut: (id: string, values: any) => void;
  removeCut?: (id: string) => void;
  addTrim: (values: any) => void;
  updateTrim: (id: string, values: any) => void;
  removeTrim?: (id: string) => void;
  addExtra: (values: any) => void;
  updateExtra: (id: string, values: any) => void;
  removeExtra?: (id: string) => void;
  addSpecification: (values: any) => void;
  updateSpecification: (id: string, values: any) => void;
  removeSpecification?: (id: string) => void;
  updateCapabilitiesInfo: (values: any) => void;
}

/**
 * Encapsulates all modal-driven handlers for creating and editing capabilities
 * (cuts, trim, extras, specifications, and catalog settings). Keeps the
 * builder page component free of repetitive CRUD boilerplate.
 * @param root0
 * @param root0.capabilities
 * @param root0.capabilityId
 * @param root0.addCut
 * @param root0.updateCut
 * @param root0.addTrim
 * @param root0.updateTrim
 * @param root0.addExtra
 * @param root0.updateExtra
 * @param root0.addSpecification
 * @param root0.updateSpecification
 * @param root0.updateCapabilitiesInfo
 * @param root0.removeCut
 * @param root0.removeTrim
 * @param root0.removeExtra
 * @param root0.removeSpecification
 */
export const useCapabilityHandlers = ({
  capabilities,
  capabilityId,
  addCut,
  updateCut,
  removeCut,
  addTrim,
  updateTrim,
  removeTrim,
  addExtra,
  updateExtra,
  removeExtra,
  addSpecification,
  updateSpecification,
  removeSpecification,
  updateCapabilitiesInfo,
}: UseCapabilityHandlersParams) => {
  const { save, ask } = useModal();
  const { push } = useToastr();

  const handleEditCapabilitiesInfo = () => {
    save({
      type: 'save',
      title: 'Catalog Settings',
      hideHeader: true,
      initialValues: {
        name: capabilities.name,
        description: capabilities.description || '',
        species: capabilities.species,
      },
      body: <CatalogInfoForm />,
      onSubmit: async (values) => {
        updateCapabilitiesInfo(values);
        push({
          title: 'Success',
          body: 'Catalog settings updated',
          bg: 'success',
        });
      },
    });
  };

  const handleAddCut = () => {
    save({
      type: 'save',
      title: 'Add Cut',
      hideHeader: true,
      initialValues: {
        name: '',
        description: '',
        groupId: '',
        basePrice: 0,
        specifications: [],
        packagingOptions: [],
        pricing: { model: '', price: 0, minimumCharge: undefined },
        specificationIds: [],
      },
      body: (
        <CutForm
          primals={capabilities.primals}
          capabilityId={capabilityId}
          specifications={capabilities.specifications}
        />
      ),
      validate: (values) => {
        const v = values as Record<string, unknown>;
        const errors: Record<string, string> = {};
        if (!((v.title as string) ?? '').trim()) {
          errors.title = 'Name is required';
        }
        if (!v.groupId) {
          errors.groupId = 'Group is required';
        }
        return errors;
      },
      onSubmit: async (values) => {
        addCut(values);
        push({ title: 'Success', body: 'Cut added', bg: 'success' });
      },
    });
  };

  const handleEditCut = (cut: Cut) => {
    // Any spec can be attached to any cut — no more type filter.
    const initialSpecIds = capabilities.specifications
      .filter((s) => s.assignedToIds?.includes(cut.id))
      .map((s) => s.id);

    save({
      type: 'save',
      title: 'Edit Cut',
      hideHeader: true,
      initialValues: {
        ...cut,
        pricing: cut.pricing ?? {
          model: 'per_pound',
          price: 0,
          minimumCharge: undefined,
        },
        specificationIds: initialSpecIds,
      },
      body: (
        <Stack spacing={2}>
          <CutForm
            primals={capabilities.primals}
            capabilityId={capabilityId}
            specifications={capabilities.specifications}
          />
          {removeCut && (
            <Button
              color="error"
              size="small"
              variant="outlined"
              onClick={() => {
                ask({
                  type: 'ask',
                  title: 'Delete Cut?',
                  body: `Are you sure you want to delete "${cut.title}"? This cannot be undone.`,
                  onConfirm: () => {
                    removeCut(cut.id);
                    push({
                      title: 'Success',
                      body: `"${cut.title}" deleted`,
                      bg: 'success',
                    });
                  },
                });
              }}
            >
              Delete Cut
            </Button>
          )}
        </Stack>
      ),
      onSubmit: async (values) => {
        const { specificationIds, ...cutValues } = values as {
          specificationIds: string[];
          [key: string]: unknown;
        };
        updateCut(cut.id, cutValues);

        const added = specificationIds.filter(
          (id) => !initialSpecIds.includes(id),
        );
        const removed = initialSpecIds.filter(
          (id) => !specificationIds.includes(id),
        );
        for (const specId of added) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec)
            updateSpecification(specId, {
              assignedToIds: [...(spec.assignedToIds || []), cut.id],
            });
        }
        for (const specId of removed) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec)
            updateSpecification(specId, {
              assignedToIds: (spec.assignedToIds || []).filter(
                (id) => id !== cut.id,
              ),
            });
        }

        push({ title: 'Success', body: 'Cut updated', bg: 'success' });
      },
    });
  };

  const handleAddTrim = () => {
    save({
      type: 'save',
      title: 'Add Trim',
      hideHeader: true,
      initialValues: {
        name: '',
        description: '',
        basePrice: 0,
        packagingOptions: [],
        pricing: { model: 'per_pound', price: 0, minimumCharge: undefined },
        specificationIds: [],
        minQty: 0,
      },
      body: (
        <TrimForm
          capabilityId={capabilityId}
          specifications={capabilities.specifications}
        />
      ),
      onSubmit: async (values) => {
        addTrim(removeTypename(values));
        push({ title: 'Success', body: 'Trim added', bg: 'success' });
      },
    });
  };

  const handleEditTrim = (trim: Trim) => {
    const initialSpecIds = capabilities.specifications
      .filter((s) => s.assignedToIds?.includes(trim.id))
      .map((s) => s.id);

    save({
      type: 'save',
      title: 'Edit Trim',
      hideHeader: true,
      initialValues: {
        ...trim,
        pricing: trim.pricing ?? {
          model: 'per_pound',
          price: 0,
          minimumCharge: undefined,
        },
        specificationIds: initialSpecIds,
      },
      body: (
        <TrimForm
          capabilityId={capabilityId}
          specifications={capabilities.specifications}
        />
      ),
      onSubmit: async (values) => {
        const { specificationIds, ...trimValues } = values as {
          specificationIds: string[];
          [key: string]: unknown;
        };
        const cleanedTrimValues = removeTypename(trimValues);
        updateTrim(trim.id, cleanedTrimValues);

        const added = specificationIds.filter(
          (id) => !initialSpecIds.includes(id),
        );
        const removed = initialSpecIds.filter(
          (id) => !specificationIds.includes(id),
        );
        for (const specId of added) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec)
            updateSpecification(specId, {
              assignedToIds: [...(spec.assignedToIds || []), trim.id],
            });
        }
        for (const specId of removed) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec)
            updateSpecification(specId, {
              assignedToIds: (spec.assignedToIds || []).filter(
                (id) => id !== trim.id,
              ),
            });
        }

        push({ title: 'Success', body: 'Trim updated', bg: 'success' });
      },
    });
  };

  const handleAddExtra = () => {
    save({
      type: 'save',
      title: 'Add Extra',
      hideHeader: true,
      initialValues: {
        name: '',
        description: '',
        basePrice: 0,
        packagingOptions: [],
        pricing: { model: 'per_pound', price: 0, minimumCharge: undefined },
        specificationIds: [],
        minQty: undefined,
      },
      body: (
        <ExtraForm
          capabilityId={capabilityId}
          specifications={capabilities.specifications}
        />
      ),
      onSubmit: async (values) => {
        addExtra(values);
        push({ title: 'Success', body: 'Extra added', bg: 'success' });
      },
    });
  };

  const handleEditExtra = (extra: Extra) => {
    const initialSpecIds = capabilities.specifications
      .filter((s) => s.assignedToIds?.includes(extra.id))
      .map((s) => s.id);

    save({
      type: 'save',
      title: 'Edit Extra',
      hideHeader: true,
      initialValues: {
        ...extra,
        pricing: extra.pricing ?? {
          model: 'per_pound',
          price: 0,
          minimumCharge: undefined,
        },
        specificationIds: initialSpecIds,
      },
      body: (
        <ExtraForm
          capabilityId={capabilityId}
          specifications={capabilities.specifications}
        />
      ),
      onSubmit: async (values) => {
        const { specificationIds, ...extraValues } = values as {
          specificationIds: string[];
          [key: string]: unknown;
        };
        updateExtra(extra.id, extraValues);

        const added = specificationIds.filter(
          (id) => !initialSpecIds.includes(id),
        );
        const removed = initialSpecIds.filter(
          (id) => !specificationIds.includes(id),
        );
        for (const specId of added) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec)
            updateSpecification(specId, {
              assignedToIds: [...(spec.assignedToIds || []), extra.id],
            });
        }
        for (const specId of removed) {
          const spec = capabilities.specifications.find((s) => s.id === specId);
          if (spec)
            updateSpecification(specId, {
              assignedToIds: (spec.assignedToIds || []).filter(
                (id) => id !== extra.id,
              ),
            });
        }

        push({ title: 'Success', body: 'Extra updated', bg: 'success' });
      },
    });
  };

  const handleAddSpecification = () => {
    save({
      type: 'save',
      title: 'Add Specification',
      hideHeader: true,
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        inputType: 'text',
        options: [],
        unit: '',
        defaultValue: '',
        required: false,
        assignedToIds: [],
        pricingAdjustments: {},
      },
      body: (
        <SpecificationForm
          cuts={capabilities.cuts}
          trim={capabilities.trim}
          extras={capabilities.extras}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addSpecification(values);
        push({ title: 'Success', body: 'Specification added', bg: 'success' });
      },
    });
  };

  const handleEditSpecification = (specification: Specification) => {
    save({
      type: 'save',
      title: 'Edit Specification',
      hideHeader: true,
      initialValues: {
        title: specification.title,
        description: specification.description || '',
        imageUrl: specification.imageUrl || '',
        inputType: specification.inputType,
        options: specification.options || [],
        unit: specification.unit || '',
        defaultValue: specification.defaultValue || '',
        required: specification.required || false,
        assignedToIds: specification.assignedToIds || [],
        pricingAdjustments: specification.pricingAdjustments || {},
      },
      body: (
        <SpecificationForm
          cuts={capabilities.cuts}
          trim={capabilities.trim}
          extras={capabilities.extras}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        updateSpecification(specification.id, values);
        push({
          title: 'Success',
          body: 'Specification updated',
          bg: 'success',
        });
      },
    });
  };

  /**
   * Opens the appropriate edit modal for an item referenced inside a question card.
   * @param itemId
   * @param itemType
   */
  const handleEditItemFromQuestion = (
    itemId: string,
    itemType: 'cut' | 'trim' | 'extra',
  ) => {
    if (itemType === 'cut') {
      const cut = capabilities.cuts.find((c) => c.id === itemId);
      if (cut) handleEditCut(cut);
    } else if (itemType === 'trim') {
      const trim = capabilities.trim.find((t) => t.id === itemId);
      if (trim) handleEditTrim(trim);
    } else if (itemType === 'extra') {
      const extra = capabilities.extras.find((e) => e.id === itemId);
      if (extra) handleEditExtra(extra);
    }
  };

  return {
    handleEditCapabilitiesInfo,
    handleAddCut,
    handleEditCut,
    handleAddTrim,
    handleEditTrim,
    handleAddExtra,
    handleEditExtra,
    handleAddSpecification,
    handleEditSpecification,
    handleEditItemFromQuestion,
  };
};
